import { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureException } from '@sentry/node';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request/index';
import { deactivateAutopayment } from '@r1-frontend/api-domru/api-pay/v1/autopayment/deactivate';
import { IAutoPaymentDeactivate } from '@r1-frontend/api-domru/api-pay/v1/autopayment/dto/IAutoPaymentDeactivate';
import { deleteCard } from '@r1-frontend/api-domru/api-pay/v1/card/remove';
import { IBindingCard } from '@r1-frontend/api-domru/api-profile/v2/info/all/dto/ResponseDto';

import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { IKebabItem } from '@r1-frontend/ui-react/components/KebabMenu';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';

import InfoSection from '~/src/features/payments-abn/components/InfoSection';
import ResultPopup, { defaultPopupState } from '~/src/features/payments-abn/components/PopupResult';
import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { selectCardsWithPaySystem } from '~/src/features/payments-abn/store/selectors/cards';
import { TState } from '~/src/store';
import { getClientAll } from '~/src/store/actions/auth/getClientAll';
import { getClientPayment } from '~/src/store/actions/auth/getClientPayment';
import { openChat } from '~/src/store/actions/chat';

import Card, { ECardKebabAction } from './card';

import * as ST from './styled';

const CardsList: FC = () => {
    const router = useRouter();
    const dispatch = useDispatch();
    const {
        auth: { accessToken },
        city: { provider: { providerId } },
    } = useSelector((state: TState) => state);
    const { isCards, isAutoPayCard, cardGroups } = useSelector(selectCardsWithPaySystem) || {};

    const [isWaitingStatus, setIsWaitingStatus] = useState(false);
    const [isOpenCardsList, setIsOpenCardsList] = useState(false);
    const [isSuccessAction, setIsSuccessAction] = useState(false);
    const [popupState, setPopupState] = useState(defaultPopupState);

    const onToggleHandler = useCallback(() => {
        setIsOpenCardsList(!isOpenCardsList);
        pushDataLayer(isOpenCardsList ? 'roll up my card' : 'expand my card', 'click');
    }, [isOpenCardsList]);

    const setUpAutoPay = useCallback((label: 'set up' | 'enable') => {
        pushDataLayer('settings autopayments', label);
        router.push('/lk/user/autopay');
    }, [router]);

    const onActionResult = useCallback((type: EResultPopupStatus, title: string) => {
        setPopupState({
            isOpen: true,
            type,
            title,
            text: type === 'error'
                ? <ST.ErrorDisconnectText>
                    Попробуйте ещё раз или напишите в&nbsp;<p onClick={() => dispatch(openChat())}>поддержку</p>
                </ST.ErrorDisconnectText>
                : '',
        });
    }, [dispatch]);

    const deactivateAutoPay = useCallback(async(card: IBindingCard) => {
        pushDataLayer('disable autopayments', 'click');
        setIsWaitingStatus(true);

        try {
            const resp = await doRequest.apiPay<IAutoPaymentDeactivate>(
                deactivateAutopayment(providerId, card.id),
            );
            const isSuccess = resp.isSuccess;

            pushDataLayer('disable autopayments', isSuccess ? 'success' : 'error');
            setIsSuccessAction(isSuccess);
            onActionResult(
                isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
                isSuccess ? 'Вы отключили автоплатёж' : 'Не удалось отключить автоплатёж',
            );
        } catch (error) {
            captureException(error);
        }

        setIsWaitingStatus(false);
    }, [providerId, accessToken, onActionResult]);

    const disconnectAutoPay = useCallback((card: IBindingCard) => {
        setPopupState({
            isOpen: true,
            title: 'Отключить автоплатёж?',
            text: 'Если отключить автоплатёж, придётся пополнять лицевой счёт вручную.',
            actions: <ST.DeactivatePopupActions>
                <BaseButton
                    type={btnTypes.ACCENT}
                    size={btnSizes.LARGE}
                    text={'Не отключать'}
                    onClick={() => setPopupState(defaultPopupState)}
                />
                <BaseButton
                    type={btnTypes.SECONDARY}
                    size={btnSizes.LARGE}
                    text={'Отключить'}
                    onClick={() => deactivateAutoPay(card)}
                    isLoading={isWaitingStatus}
                />
            </ST.DeactivatePopupActions>,
        });
    }, [deactivateAutoPay, isWaitingStatus]);

    const deleteCardHandler = useCallback(async(card: IBindingCard) => {
        setIsWaitingStatus(true);

        try {
            const resp = await doRequest.apiPay(
                deleteCard(providerId, card.id),
            );
            const isSuccess = resp.isSuccess;

            pushDataLayer('delete card', isSuccess ? 'success' : 'error');
            setIsSuccessAction(isSuccess);
            onActionResult(
                isSuccess ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
                isSuccess ? `Удалили карту ${card.numberWithPaySystem}` : 'Не удалось удалить карту',
            );
            dispatch(getClientPayment());
        } catch (error) {
            captureException(error);
        }

        setIsWaitingStatus(false);
    }, [providerId, accessToken, onActionResult, dispatch]);

    const removeCard = useCallback((card: IBindingCard) => {
        pushDataLayer('delete card', 'click');
        setPopupState({
            isOpen: true,
            title: 'Удалить карту?',
            text: 'Тогда в следующий раз придётся вводить данные карты вручную.',
            actions: <ST.DeactivatePopupActions>
                <BaseButton
                    type={btnTypes.ACCENT}
                    size={btnSizes.LARGE}
                    text={'Не удалять'}
                    onClick={() => setPopupState(defaultPopupState)}
                />
                <BaseButton
                    type={btnTypes.SECONDARY}
                    size={btnSizes.LARGE}
                    text={'Удалить'}
                    onClick={() => deleteCardHandler(card)}
                    isLoading={isWaitingStatus}
                />
            </ST.DeactivatePopupActions>,
        });
    }, [deleteCardHandler, isWaitingStatus]);

    const onSelectMenu = useCallback((card: IBindingCard, item?: IKebabItem) => {
        switch (item?.value) {
            case ECardKebabAction.CONNECT:
                setUpAutoPay('enable');
                break;
            case ECardKebabAction.SETUP:
                setUpAutoPay('set up');
                break;
            case ECardKebabAction.DISCONNECT:
                disconnectAutoPay(card);
                break;
            case ECardKebabAction.REMOVE:
                removeCard(card);
                break;
        }
    }, [removeCard, disconnectAutoPay, setUpAutoPay]);

    const onClosePopup = useCallback(() => {
        isSuccessAction && dispatch(getClientAll());
        setPopupState(defaultPopupState);
    }, [dispatch, isSuccessAction]);

    return (isCards
        ? <InfoSection
            isOpen={isOpenCardsList}
            title={'Мои карты'}
            onToggle={onToggleHandler}
        >
            <InfoSection.Content isAccordion>
                {cardGroups?.map(group => {
                    const { blockTitle, cardsList } = group;

                    return cardsList.length
                        ? <ST.CardsBlock key={group.blockTitle}>
                            {isAutoPayCard && <ST.BlockTitle>{blockTitle}</ST.BlockTitle>}
                            {cardsList.map(card => (
                                <Card
                                    key={card.id}
                                    card={card}
                                    onSelectMenu={(item?: IKebabItem) => onSelectMenu(card, item)}
                                />
                            ))}
                        </ST.CardsBlock>
                        : null;
                })}
            </InfoSection.Content>
            <ResultPopup {...popupState} onClose={onClosePopup}/>
        </InfoSection>
        : null
    );
};

export default CardsList;
